import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { GefahrenQuestion } from "./types";

/**
 * Renders a single Gefahren / Vertragsdaten question's control input.
 * Extracted from `GefahrenSection.tsx` so that other steps
 * (Zahlung, per-Risk Versicherungsumfang) can render the same widgets
 * without duplicating the type-/regex-based switch logic.
 */

const useStyles = makeStyles({
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  toggleRow: {
    display: "inline-flex",
    columnGap: "8px",
  },
  toggleBtn: {
    cursor: "pointer",
    appearance: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 600,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "16px",
    paddingRight: "16px",
    minWidth: "76px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    transitionProperty: "background-color, color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  toggleBtnYes: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarGreen),
  },
  toggleBtnNo: {
    backgroundColor: "#a1331c",
    color: BRAND.white,
    ...shorthands.borderColor("#a1331c"),
  },
  infobox: {
    backgroundColor: "#fff5e6",
    color: "#7a4a10",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.88rem",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
  },
  infoIcon: {
    color: BRAND.gold,
    flexShrink: 0,
    marginTop: "2px",
  },
});

const SB_OPTIONS_VOLLKASKO_INKL_TEILKASKO: { value: string; label: string }[] = [
  "150/OHNE",
  "150/150",
  "300/OHNE",
  "300/150",
  "300/300",
  "500/OHNE",
  "500/150",
  "500/500",
  "1000/OHNE",
  "1000/150",
  "1000/1000",
  "2500/2500",
].map((v) => ({ value: v, label: v }));

const SB_OPTIONS_TEILKASKO = [
  { value: "OHNE", label: "OHNE" },
  { value: "150", label: "150 €" },
  { value: "500", label: "500 €" },
  { value: "1000", label: "1.000 €" },
  { value: "2500", label: "2.500 €" },
];

const ZAHLWEISE_OPTIONS = [
  { value: "jaehrlich", label: "jährlich" },
  { value: "halbjaehrlich", label: "halbjährlich" },
  { value: "vierteljaehrlich", label: "vierteljährlich" },
  { value: "monatlich", label: "monatlich (nur mit Einzugsermächtigung)" },
];

const ZAHLUNGSART_OPTIONS = [
  { value: "abbuchung_versicherer", label: "Abbuchung durch Versicherer" },
  { value: "abbuchung_courte", label: "Abbuchung durch Courté" },
  { value: "ueberweisung_versicherer", label: "Überweisung an Versicherer" },
  { value: "ueberweisung_courte", label: "Überweisung an Courté" },
];

const isYesNo = (m?: string | null) => (m ?? "").toLowerCase().includes("ja/nein");
const isInfo = (frage: string) => frage.toLowerCase().startsWith("infofenster:");
const isDateField = (frage: string) => {
  const lower = frage.toLowerCase();
  return (
    lower.includes("beginn") ||
    lower.includes("ablauf") ||
    lower.includes("fälligkeit")
  );
};
const isYearsField = (m?: string | null) => (m ?? "").toLowerCase().includes("anzahl jahre");
const isDropdown = (m?: string | null) =>
  (m ?? "").toLowerCase().includes("drop down") || (m ?? "").toLowerCase().includes("liste");

type Props = {
  question: GefahrenQuestion;
  value: string;
  onChange: (key: string, value: string) => void;
};

const GefahrenControl = ({ question: q, value, onChange }: Props) => {
  const styles = useStyles();
  const key = q.key!;

  if (isInfo(q.frage)) {
    return (
      <div className={styles.infobox}>
        <span className={styles.infoIcon}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="9" r="7" />
            <path d="M9 8v5" />
            <circle cx="9" cy="5.5" r="0.6" fill="currentColor" />
          </svg>
        </span>
        <span>{q.frage.replace(/^Infofenster:\s*/i, "")}</span>
      </div>
    );
  }
  if (isYesNo(q.merkmal)) {
    return (
      <div className={styles.toggleRow}>
        <button
          type="button"
          onClick={() => onChange(key, value === "ja" ? "" : "ja")}
          className={mergeClasses(styles.toggleBtn, (value === "ja") && styles.toggleBtnYes)}
          aria-pressed={value === "ja"}
        >
          Ja
        </button>
        <button
          type="button"
          onClick={() => onChange(key, value === "nein" ? "" : "nein")}
          className={mergeClasses(styles.toggleBtn, (value === "nein") && styles.toggleBtnNo)}
          aria-pressed={value === "nein"}
        >
          Nein
        </button>
      </div>
    );
  }
  if (q.frage.toLowerCase().startsWith("selbstbeteiligung voll")) {
    return (
      <select
        className={styles.input}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
      >
        <option value="">— bitte auswählen (VK-SB / TK-SB) —</option>
        {SB_OPTIONS_VOLLKASKO_INKL_TEILKASKO.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (q.frage.toLowerCase().startsWith("selbstbeteiligung teilkasko")) {
    return (
      <select
        className={styles.input}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
      >
        <option value="">— bitte auswählen —</option>
        {SB_OPTIONS_TEILKASKO.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (q.frage.toLowerCase().startsWith("zahlweise")) {
    return (
      <select
        className={styles.input}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
      >
        <option value="">— bitte auswählen —</option>
        {ZAHLWEISE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (q.frage.toLowerCase().startsWith("zahlungsart")) {
    return (
      <select
        className={styles.input}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
      >
        <option value="">— bitte auswählen —</option>
        {ZAHLUNGSART_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (isDateField(q.frage)) {
    return (
      <input
        type="date"
        className={styles.input}
        value={value}
        onChange={(e) => onChange(key, e.target.value)}
      />
    );
  }
  if (isYearsField(q.merkmal)) {
    return (
      <input
        type="number"
        min={0}
        className={styles.input}
        value={value}
        placeholder="z. B. 12"
        onChange={(e) => onChange(key, e.target.value)}
      />
    );
  }
  if (
    q.frage.toLowerCase().includes("bemerkung") ||
    q.frage.toLowerCase().includes("rechnungsempfänger") ||
    q.frage.toLowerCase().includes("bankverbindung")
  ) {
    return (
      <textarea
        rows={3}
        className={styles.input}
        value={value}
        placeholder={q.merkmal ?? ""}
        onChange={(e) => onChange(key, e.target.value)}
        style={{ minHeight: 80, resize: "vertical" }}
      />
    );
  }
  if (isDropdown(q.merkmal)) {
    return (
      <input
        className={styles.input}
        type="text"
        value={value}
        placeholder={q.merkmal ?? ""}
        onChange={(e) => onChange(key, e.target.value)}
      />
    );
  }
  return (
    <input
      className={styles.input}
      type="text"
      value={value}
      placeholder={q.merkmal ?? ""}
      onChange={(e) => onChange(key, e.target.value)}
    />
  );
};

export const isCascoSelector = (frage: string) =>
  frage.trim().toLowerCase() === "auswahl kaskoversicherung";
export const isInfoQuestion = isInfo;

export default GefahrenControl;
